import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
    setIsCategoryOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group"
          >
            <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 font-heading font-bold text-lg md:text-xl">
              精
            </div>
            <span className={`font-heading font-bold text-lg md:text-xl tracking-wide transition-colors ${
              isScrolled ? 'text-foreground-900' : 'text-background-50'
            }`}>
              精品久久久
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/'
                  ? 'text-primary-600'
                  : isScrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-background-50/90 hover:text-background-50'
              }`}
            >
              首页
            </Link>

            {/* Category Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                  location.pathname.startsWith('/category')
                    ? 'text-primary-600'
                    : isScrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-background-50/90 hover:text-background-50'
                }`}
              >
                分类浏览
                <i className={`ri-arrow-down-s-line text-base transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isCategoryOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-lg border border-background-200/70 py-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className={`${cat.icon} mr-2 text-primary-500`}></i>
                      {cat.name}
                      <span className="text-foreground-400 text-xs ml-2">({cat.articleCount})</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/about'
                  ? 'text-primary-600'
                  : isScrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-background-50/90 hover:text-background-50'
              }`}
            >
              关于我们
            </Link>
          </div>

          {/* Search + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <button
              className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-all cursor-pointer ${
                isScrolled
                  ? 'border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                  : 'border-background-50/30 text-background-50/80 hover:border-background-50/60 hover:text-background-50'
              }`}
            >
              <i className="ri-search-line"></i>
              <span>搜索</span>
            </button>

            <button
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                isScrolled ? 'text-foreground-700' : 'text-background-50'
              }`}
            >
              <i className={`text-xl ${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className={`block px-4 py-3 rounded-lg text-sm font-medium cursor-pointer ${
                location.pathname === '/' ? 'bg-primary-50 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              首页
            </Link>
            <div className="px-4 py-3 text-sm font-medium text-foreground-500">分类浏览</div>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`block px-4 py-2.5 pl-8 rounded-lg text-sm cursor-pointer ${
                  location.pathname === `/category/${cat.slug}`
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                <i className={`${cat.icon} mr-2 text-primary-500`}></i>
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className={`block px-4 py-3 rounded-lg text-sm font-medium cursor-pointer ${
                location.pathname === '/about' ? 'bg-primary-50 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              关于我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}